import SensorStatusRow from "./SensorStatusRow";
import ConfigurableStatusRow from "./ConfigurableStatusRow";
import { VsdStatusRow } from "./VsdStatusRow";
import {
  TableBody,
  TableRow,
  TableCell,
  StatusIndicator,
  StatusText,
} from "styles/devices/deviceDetails.styles";
import { ParamConnectionSlot } from "styles/devices/deviceTable.styles";
import { StatusParametersTableProps } from "types/device.types";
import {
  CELL_OUTPUT_RESOLUTION,
  ORP_SETPOINT_RESOLUTION,
} from "utils/setpointResolution";

type StatusParametersRowsProps = Pick<
  StatusParametersTableProps,
  | "shadow"
  | "isEditingParams"
  | "updatingParams"
  | "pendingParams"
  | "handleParamChange"
  | "handleToggleSensorConnection"
  | "editingParameter"
>;

export function StatusParametersRows({
  shadow,
  isEditingParams,
  updatingParams,
  pendingParams,
  handleParamChange,
  handleToggleSensorConnection,
  editingParameter,
}: StatusParametersRowsProps) {
  const flowState = shadow.flow === 1 && shadow.nipperConnection === 1;
  const nipperConnection = shadow.nipperConnection === 1;
  const vsdSpeed = shadow.vsdSpeed || 0;
  const vsdIsConnected = shadow.vsdIsConnected || false;
  const isRowEditing = (param: string) =>
    isEditingParams &&
    (editingParameter === null || editingParameter === param);

  return (
    <TableBody>
      <TableRow connected={true}>
        <TableCell>
          <strong>Flow</strong>
        </TableCell>
        <TableCell>
          <StatusText connected={flowState}>
            {flowState ? "Running" : "No Flow"}
          </StatusText>
        </TableCell>
        <TableCell>-</TableCell>
        <TableCell>
          <ParamConnectionSlot>
            <StatusIndicator connected={nipperConnection} />
            <StatusText connected={nipperConnection}>
              {nipperConnection
                ? "Chlorinator Connected"
                : "Chlorinator Disconnected"}
            </StatusText>
          </ParamConnectionSlot>
        </TableCell>
      </TableRow>

      <SensorStatusRow
        name="pH"
        currentValue={flowState ? shadow.ph : null}
        setPoint={shadow.phSetpoint}
        isConnected={shadow.phConnected}
        isEditing={isRowEditing("phSetpoint")}
        pendingValue={pendingParams.phSetpoint}
        pendingConnected={pendingParams.phConnected}
        min={7.0}
        max={7.8}
        step={0.1}
        onValueChange={(value) => handleParamChange("phSetpoint", value)}
        onConnectionToggle={() => handleToggleSensorConnection("phConnected")}
        disabled={updatingParams}
      />

      <SensorStatusRow
        name="ORP"
        currentValue={flowState ? shadow.orp : null}
        setPoint={shadow.orpSetpoint}
        isConnected={shadow.orpConnected}
        isEditing={isRowEditing("orpSetpoint")}
        pendingValue={pendingParams.orpSetpoint}
        pendingConnected={pendingParams.orpConnected}
        min={ORP_SETPOINT_RESOLUTION.min}
        max={ORP_SETPOINT_RESOLUTION.max}
        step={ORP_SETPOINT_RESOLUTION.step}
        onValueChange={(value) => handleParamChange("orpSetpoint", value)}
        onConnectionToggle={() => handleToggleSensorConnection("orpConnected")}
        disabled={updatingParams}
      />

      <SensorStatusRow
        name="Temperature"
        currentValue={flowState ? shadow.temp : null}
        hideSetPoint={true}
        isConnected={shadow.tempConnected}
        isEditing={isRowEditing("tempSetpoint")}
        pendingValue={pendingParams.tempSetpoint}
        pendingConnected={pendingParams.tempConnected}
        min={15}
        max={40}
        step={0.5}
        onValueChange={(value) => handleParamChange("tempSetpoint", value)}
        onConnectionToggle={() => handleToggleSensorConnection("tempConnected")}
        disabled={updatingParams}
      />

      <SensorStatusRow
        name="Salinity"
        currentValue={flowState ? shadow.salinity : null}
        hideSetPoint={true}
        isConnected={shadow.salinityConnected}
        isEditing={isRowEditing("salinitySetpoint")}
        pendingValue={pendingParams.salinitySetpoint}
        pendingConnected={pendingParams.salinityConnected}
        min={0}
        max={10000}
        step={100}
        onValueChange={(value) => handleParamChange("salinitySetpoint", value)}
        onConnectionToggle={() =>
          handleToggleSensorConnection("salinityConnected")
        }
        disabled={updatingParams}
      />

      <VsdStatusRow
        currentValue={flowState ? vsdSpeed : null}
        isConnected={vsdIsConnected}
        pendingValue={pendingParams.vsdSpeed}
        pendingConnected={pendingParams.vsdIsConnected}
        isEditing={isRowEditing("vsdSpeed")}
        disabled={updatingParams}
        onValueChange={(value) => handleParamChange("vsdSpeed", value)}
        onConnectionToggle={() =>
          handleParamChange("vsdIsConnected", !pendingParams.vsdIsConnected)
        }
      />

      <ConfigurableStatusRow
        name="Cell Output"
        currentValue={flowState ? shadow.cellOutput : null}
        setPoint={shadow.setCellOutput}
        isConfigured={true}
        isEditing={isRowEditing("setCellOutput")}
        pendingValue={pendingParams.setCellOutput}
        min={CELL_OUTPUT_RESOLUTION.min}
        max={CELL_OUTPUT_RESOLUTION.max}
        step={CELL_OUTPUT_RESOLUTION.step}
        suffix="%"
        onValueChange={(value: number) =>
          handleParamChange("setCellOutput", value)
        }
        alwaysActive={true}
        disabled={updatingParams || pendingParams.orpConnected}
        disabledMessage={
          pendingParams.orpConnected
            ? "Cell Output cannot be modified while ORP sensor is connected"
            : ""
        }
      />
    </TableBody>
  );
}
